import type { ID } from '@octopus-do/octopus-project-core'
import { selectIsLoading } from '@store/loaders'
import { migrationProjectSelector } from '@store/migrationSelectors/projectSelectors'
import { DashboardActions } from '@store/reducers/dashboard/actions'
import { selectDashboard } from '@store/reducers/dashboard/selectors'
import type { ProjectMeta } from '@store/reducers/manager/meta/types'
import {
  selectTemplateSaveError,
  TemplatesActions,
} from '@store/reducers/templates'
import { InternalTemplatesActions } from '@store/reducers/templates/actions'
import type { AppState } from '@store/types'
import { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { isPaidPlan } from '@/constants/userSubscriptionPlans'
import { useAuth } from '@/hooks'

interface WorkspaceOption {
  id: ID
  title: string
}

export const useSaveAsTemplate = () => {
  const dispatch = useDispatch()

  const projectId = useSelector(migrationProjectSelector.getProjectId) as ID
  const projectTitle = useSelector(migrationProjectSelector.getProjectTitle)
  const { team } = useSelector<AppState, ProjectMeta>(
    (state) => state.manager.meta
  )

  const { planId } = useAuth()

  // The project's own workspace is not a reliable source: a project can live in
  // the personal workspace while the template has to land in a team. So the
  // modal asks for the user's workspaces — through the dashboard's own saga,
  // which fetches them into the store; opened from the editor, the store is
  // usually still empty.
  const all = useSelector(selectDashboard)
  const fetching = useSelector(selectIsLoading('workspaces'))
  const [requested, setRequested] = useState(false)

  useEffect(() => {
    dispatch(DashboardActions.init())
    dispatch(InternalTemplatesActions.saveReset())
    setRequested(true)
  }, [dispatch])

  // The personal workspace is a target only on a paid plan; a team is a target
  // for any of its members.
  const workspaces = useMemo<WorkspaceOption[]>(
    () =>
      all
        .filter((workspace) => !workspace.expired)
        .filter((workspace) => workspace.type === 'team' || isPaidPlan(planId))
        .map(({ id, title }) => ({ id, title })),
    [all, planId]
  )

  const loadingWorkspaces = !requested || fetching

  const [name, setName] = useState(projectTitle ?? '')
  const [chosenWorkspaceId, setWorkspaceId] = useState<ID | null>(null)
  const [localError, setLocalError] = useState<string | null>(null)

  // Default to where the project already is; the first option otherwise.
  const workspaceId =
    chosenWorkspaceId ??
    workspaces.find((option) => option.id === team?.id)?.id ??
    workspaces[0]?.id ??
    null

  const saving = useSelector(selectIsLoading('template_save'))
  const saveError = useSelector(selectTemplateSaveError)

  const noWorkspace = !loadingWorkspaces && workspaces.length === 0

  const error =
    localError ??
    saveError ??
    (noWorkspace ? 'No workspace to save the template into.' : null)

  const changeName = (value: string) => {
    setLocalError(null)
    setName(value)
  }

  const save = () => {
    const trimmed = name.trim()

    if (!trimmed) {
      setLocalError('Please enter a template name.')

      return
    }

    if (!workspaceId) {
      setLocalError('Please choose a workspace.')

      return
    }

    dispatch(TemplatesActions.save({ projectId, name: trimmed, workspaceId }))
  }

  return {
    name,
    workspaces,
    workspaceId,
    loadingWorkspaces,
    saving,
    error,
    changeName,
    setWorkspaceId,
    save,
  }
}
